import {
    Locale,
    SERVER_CACHE_TTL_SECONDS,
    ServerTokenCacheKey,
    REDIS_KEY_SEPARATOR,
    RedisConfig,
} from '@headless-workspace/config';
import { initRedis } from '@headless-workspace/utils';
import { AccessTokenCacheDTO, AccessTokenLastUpdateCacheDTO } from './CacheDTO';

const serverCacheMap: Map<string, string | number | undefined> = new Map();

const CACHE_SERVICE_TOKEN_KEY = ['server', 'token'].join(REDIS_KEY_SEPARATOR);

const createServerCacheKey = (locale: Locale, key: string): string => {
    return [RedisConfig.GlobalPrefix, locale, CACHE_SERVICE_TOKEN_KEY, key].join(REDIS_KEY_SEPARATOR);
};

export const ServerTokenCache = {
    async getAccessToken(locale: Locale): Promise<string | undefined> {
        const accessTokenCacheKey = createServerCacheKey(locale, ServerTokenCacheKey.AccessToken);

        const redisInstance = await initRedis();
        // If redis available, get access token from Redis
        const cachedAccessToken = await redisInstance?.get(accessTokenCacheKey);
        if (cachedAccessToken) {
            return cachedAccessToken;
        }

        // If redis not available, get access token from memory
        const memoryAccessToken = serverCacheMap.get(accessTokenCacheKey);
        const parseResult = AccessTokenCacheDTO.safeParse(memoryAccessToken);
        return parseResult.success ? parseResult.data : undefined;
    },

    async getAccessTokenTTL(locale: Locale): Promise<number | undefined> {
        const redisInstance = await initRedis();
        // If redis is available, return Infinity as TTL will be handled by Redis
        if (redisInstance) {
            return Infinity;
        }

        const cacheKey = createServerCacheKey(locale, ServerTokenCacheKey.AccessTokenLastUpdate);
        const cachedAccessTokenLastUpdate = serverCacheMap.get(cacheKey);
        const parseResult = AccessTokenLastUpdateCacheDTO.safeParse(cachedAccessTokenLastUpdate);
        return parseResult.success ? parseResult.data : undefined;
    },

    async setAccessToken(accessToken: string, locale: Locale): Promise<void> {
        const accessTokenCacheKey = createServerCacheKey(locale, ServerTokenCacheKey.AccessToken);

        const redisInstance = await initRedis();
        // If redis available, cache access token in Redis
        if (redisInstance) {
            const result = await redisInstance.setex(accessTokenCacheKey, SERVER_CACHE_TTL_SECONDS, accessToken);
            if (result === 'OK') {
                return;
            }
        }

        // If redis not available, cache access token in memory
        const accessTokenLastUpdateCacheKey = createServerCacheKey(locale, ServerTokenCacheKey.AccessTokenLastUpdate);
        serverCacheMap.set(accessTokenCacheKey, accessToken);
        serverCacheMap.set(accessTokenLastUpdateCacheKey, Date.now());
    },
};
